import {
  AssignManagerTenantsResponse,
  CreateManagerResponse,
  DeleteManagerResponse,
  ManagerAuditListResponse,
  ManagerBonusView,
  ManagerCardResponse,
  ManagerEarningsResponse,
  ManagerListResponse,
  ManagersOverviewResponse,
  ManagerView,
  type CancelManagerBonusRequest,
  type CreateManagerBonusRequest,
  type CreateManagerRequest,
  type ManagerAuditListQuery,
  type SetManagerCommissionRequest,
  type UpdateManagerRequest,
} from '@odonix/contracts';
import { periodQuery, type Period } from '../../ui/period.js';
import { TenantsError } from './tenants.js';

/**
 * Менеджеры Odonix в платформенной админке: `/v1/managers/*`. Ошибки — тем же
 * классом, что у клиник: экран показывает `message` сервера и не различает,
 * из какого модуля он пришёл.
 */

type ApiFetch = (path: string, init?: RequestInit) => Promise<Response>;

export interface ManagersApi {
  list(options?: { includeDeleted?: boolean }): Promise<ManagerView[]>;
  overview(period: Period): Promise<ManagersOverviewResponse>;
  card(managerId: string, period: Period): Promise<ManagerCardResponse>;
  earnings(managerId: string, period: Period): Promise<ManagerEarningsResponse>;
  create(body: CreateManagerRequest): Promise<CreateManagerResponse>;
  update(managerId: string, body: UpdateManagerRequest): Promise<ManagerView>;
  setCommission(managerId: string, body: SetManagerCommissionRequest): Promise<ManagerView>;
  addBonus(managerId: string, body: CreateManagerBonusRequest): Promise<ManagerBonusView>;
  cancelBonus(managerId: string, bonusId: string, body: CancelManagerBonusRequest): Promise<void>;
  assignTenants(managerId: string, tenantIds: readonly string[]): Promise<AssignManagerTenantsResponse>;
  remove(managerId: string, transferToManagerId: string | null): Promise<DeleteManagerResponse>;
  audit(query: Partial<ManagerAuditListQuery>): Promise<ManagerAuditListResponse>;
}

async function toError(response: Response): Promise<TenantsError> {
  const body = (await response.json().catch(() => null)) as {
    code?: unknown;
    message?: unknown;
    fields?: Record<string, string>;
  } | null;
  return typeof body?.code === 'string' && typeof body.message === 'string'
    ? new TenantsError(body.code, body.message, body.fields)
    : new TenantsError('UNKNOWN', 'Сервер не ответил. Проверьте связь и повторите');
}

export function createManagersApi(apiFetch: ApiFetch): ManagersApi {
  async function send(path: string, init?: RequestInit): Promise<unknown> {
    const response = await apiFetch(path, init);
    if (!response.ok) throw await toError(response);
    if (response.status === 204) return null;
    return response.json();
  }

  function json(method: string, body: unknown): RequestInit {
    return { method, headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) };
  }

  const id = (value: string) => encodeURIComponent(value);

  return {
    async list(options = {}) {
      const query = options.includeDeleted === true ? '?include=deleted' : '';
      return ManagerListResponse.parse(await send(`/v1/managers${query}`)).managers;
    },

    async overview(period) {
      return ManagersOverviewResponse.parse(await send(`/v1/managers/overview?${periodQuery(period)}`));
    },

    async card(managerId, period) {
      return ManagerCardResponse.parse(await send(`/v1/managers/${id(managerId)}?${periodQuery(period)}`));
    },

    async earnings(managerId, period) {
      return ManagerEarningsResponse.parse(await send(`/v1/managers/${id(managerId)}/earnings?${periodQuery(period)}`));
    },

    async create(body) {
      return CreateManagerResponse.parse(await send('/v1/managers', json('POST', body)));
    },

    async update(managerId, body) {
      return ManagerView.parse(await send(`/v1/managers/${id(managerId)}`, json('PATCH', body)));
    },

    async setCommission(managerId, body) {
      return ManagerView.parse(await send(`/v1/managers/${id(managerId)}/commission`, json('PUT', body)));
    },

    async addBonus(managerId, body) {
      return ManagerBonusView.parse(await send(`/v1/managers/${id(managerId)}/bonuses`, json('POST', body)));
    },

    async cancelBonus(managerId, bonusId, body) {
      await send(`/v1/managers/${id(managerId)}/bonuses/${id(bonusId)}/cancel`, json('POST', body));
    },

    async assignTenants(managerId, tenantIds) {
      return AssignManagerTenantsResponse.parse(
        await send(`/v1/managers/${id(managerId)}/tenants`, json('POST', { tenantIds })),
      );
    },

    async remove(managerId, transferToManagerId) {
      return DeleteManagerResponse.parse(
        await send(`/v1/managers/${id(managerId)}`, json('DELETE', { transferToManagerId })),
      );
    },

    async audit(query) {
      const params = new URLSearchParams();
      if (query.managerId !== undefined) params.set('managerId', query.managerId);
      if (query.action !== undefined) params.set('action', query.action);
      if (query.tenantId !== undefined) params.set('tenantId', query.tenantId);
      if (query.from !== undefined) params.set('from', query.from);
      if (query.to !== undefined) params.set('to', query.to);
      if (query.cursor !== undefined) params.set('cursor', query.cursor);
      if (query.limit !== undefined) params.set('limit', String(query.limit));
      return ManagerAuditListResponse.parse(await send(`/v1/managers/audit?${params.toString()}`));
    },
  };
}
